import { useRef, useCallback, useEffect, RefObject, useState } from "react"; // useCallback と useEffect をインポート
import { Marker } from "maplibre-gl";
import type * as maplibregl from "maplibre-gl";
import { createRoot, Root } from "react-dom/client";
import { DIGITAL_SIGNAGE_LOCATION } from "@/config/constants";
import CurrentLocationMarker from "@/components/CurrentLocationMarker";
import SignageSpeechBubble from "@/components/SignageSpeechBubble";

export const useCurrentLocationDisplay = (
  map: RefObject<maplibregl.Map | null>,
  currentFloorId: string,
  isMobile: boolean = false,
  isRouteActive: boolean = false,
) => {
  const [currentLocationInfo, setCurrentLocationInfo] = useState<{
    position: {
      x: number;
      y: number;
    };
    floorId: string;
  } | null>(null);
  const currentLocationMarker = useRef<maplibregl.Marker | null>(null);
  const currentLocationMarkerRoot = useRef<Root | null>(null);
  const isSignage = useRef<boolean>(false);
  // SP経路表示中の非表示判定は ref で最新値を参照する。フロアジャンプ吹き出しは経路探索時点の
  // handleFloorChange を保持するため、クロージャの isRouteActive だと false のまま再表示してしまう (#591)
  const hideDuringRouteRef = useRef<boolean>(isMobile && isRouteActive);
  useEffect(() => {
    hideDuringRouteRef.current = isMobile && isRouteActive;
  }, [isMobile, isRouteActive]);

  function runTaskRepeatedly(task: (counter: number) => void, delay: number, repetitions: number) {
    let counter = 0; // 実行回数を数えるカウンター

    // setInterval を開始
    // 実行IDを intervalId に保存
    const intervalId = setInterval(() => {
      // 1. まずカウンターを増やす
      counter++;

      // 2. 渡されたタスクを実行
      // (何回目に実行されたかをタスクに渡すことも可能)
      task(counter);

      // 3. カウンターが指定回数に達したかチェック
      if (counter >= repetitions) {
        // 4. 達していたら、setInterval を停止する
        clearInterval(intervalId);
      }
    }, delay);

    // (オプション)
    // 外部から強制的に停止したい場合に備えて、IDを返す
    return intervalId;
  }

  const initializeCurrentLocation = () => {
    if (!map.current) {
      clearCurrentLocationMarker();
      return;
    }

    if (isSignage.current) {
      setCurrentLocationInfo(DIGITAL_SIGNAGE_LOCATION);
    } else {
      //現在地がスマホでも取得できるようになったらここで現残地変更イベントにsetCurrentLocationInfoをアタッチすればいい
      runTaskRepeatedly(
        (counter) =>
          setCurrentLocationInfo({
            position: {
              x: Math.cos(counter * 0.1) * 0.003 + 0.005,
              y: Math.sin(counter * 0.1) * 0.003 + 0.005,
            },
            floorId: "main-3",
          }),
        100,
        500,
      );
      console.warn(
        "デジタルサイネージで起動していない場合には現在地が取得できないため現在地表示機能は使用できません。",
      );
    }
  };

  // clear処理をメモ化
  const clearCurrentLocationMarker = useCallback(() => {
    if (currentLocationMarkerRoot.current) {
      const root = currentLocationMarkerRoot.current;

      setTimeout(() => {
        root.unmount();
      }, 0);

      currentLocationMarkerRoot.current = null;
    }

    if (currentLocationMarker.current) {
      currentLocationMarker.current.remove();
      currentLocationMarker.current = null;
    }
  }, []); // 依存配列は空

  const createCurrentLocationMarker = useCallback((): void => {
    if (!currentLocationInfo) return;

    clearCurrentLocationMarker();

    const container = document.createElement("div");
    currentLocationMarkerRoot.current = createRoot(container);
    currentLocationMarkerRoot.current.render(
      isMobile ? <SignageSpeechBubble /> : <CurrentLocationMarker />,
    );

    currentLocationMarker.current = new Marker({
      element: container,
      anchor: "center",
    }).setLngLat([currentLocationInfo.position.x, currentLocationInfo.position.y]);
  }, [currentLocationInfo, clearCurrentLocationMarker, isMobile]);

  // PC の現在地マーカーの脈動は数回で止まるので、現在地へ視線を戻したいときに key を変えて再生し直す
  const pulseKeyRef = useRef(0);
  const replayCurrentLocationPulse = useCallback(() => {
    if (isMobile || !currentLocationMarkerRoot.current) return;
    pulseKeyRef.current += 1;
    currentLocationMarkerRoot.current.render(<CurrentLocationMarker key={pulseKeyRef.current} />);
  }, [isMobile]);

  const updateCurrentLocationMarkerForFloor = useCallback(
    (floorId: string): void => {
      //マーカーが未作成なら何もしない
      if (!map.current || !currentLocationMarker.current) return;

      // SP経路表示中は非表示
      if (hideDuringRouteRef.current) {
        currentLocationMarker.current.remove();
        return;
      }

      // フロアIDが現在地のフロアと一致する場合のみマップに追加
      if (floorId === currentLocationInfo?.floorId) {
        currentLocationMarker.current.addTo(map.current);
      } else {
        currentLocationMarker.current.remove();
      }
    },
    [map, currentLocationInfo],
  );

  // SP経路表示状態が変わったときにマーカーを即時更新
  useEffect(() => {
    if (!map.current || !currentLocationMarker.current) return;
    if (isMobile && isRouteActive) {
      currentLocationMarker.current.remove();
    } else if (currentLocationInfo?.floorId === currentFloorId) {
      currentLocationMarker.current.addTo(map.current);
    }
  }, [isMobile, isRouteActive, currentFloorId, currentLocationInfo, map]); // 依存配列を更新

  useEffect(() => {
    if (!map.current) return;

    if (currentLocationInfo) {
      const x = currentLocationInfo.position.x;
      const y = currentLocationInfo.position.y;

      if (currentLocationMarker.current) {
        currentLocationMarker.current.setLngLat([x, y]);
      } else {
        createCurrentLocationMarker();
      }

      updateCurrentLocationMarkerForFloor(currentFloorId);
    } else {
      clearCurrentLocationMarker();
    }
  }, [
    currentLocationInfo,
    clearCurrentLocationMarker,
    createCurrentLocationMarker,
    currentFloorId,
    map,
    updateCurrentLocationMarkerForFloor,
  ]);

  // クリーンアップ (コンポーネントがアンマウントされた時に実行)
  useEffect(() => {
    return () => {
      clearCurrentLocationMarker();
    };
  }, [clearCurrentLocationMarker]);

  //サイネージかどうかの設定
  useEffect(() => {
    isSignage.current = true; //なんかURLのパラメータとかでサイネージでアプリ起動しているかどうかの条件を使う
  }, []);

  return {
    initializeCurrentLocation,
    updateCurrentLocationMarkerForFloor,
    replayCurrentLocationPulse,
  };
};
